import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SerializationConsole.css';

// Serialization console — a React port of Landon's GENF Bag Console (borrowed UI), wired to our
// admin endpoints. Two panels: top shows Live/Search results, a record detail, and a tagUID
// collision alert; bottom has Live/Search controls (left) and View/Create/Patch/Delete record
// actions (right). Create runs the store-or-409 scan-in; a serial/tag conflict raises the collision
// alert with quick "Load existing / Patch existing" actions. Physical tag-writing stays in the RFID
// tool — this is the database side of serialization.

const RESULT_COLS = ['id', 'serialNumber', 'tagUid', 'model', 'batchId', 'ac', 'status', 'scanTime'];
const DETAIL_ORDER = ['id', 'serialNumber', 'tagUid', 'model', 'batchId', 'ac', 'schoolName', 'status', 'students', 'customName', 'scanTime'];
const emptyForm = { tagId: '', sku: '', serialNumber: '', batchId: '', ac: '' };

function SerializationConsole() {
  const api = process.env.REACT_APP_API_ENDPOINT;
  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': 'Basic ' + btoa(`${localStorage.getItem('admin_username')}:${localStorage.getItem('admin_password')}`),
  });

  const [mode, setMode] = useState('live');            // live | search
  const [actionMode, setActionMode] = useState('view'); // view | create | patch | delete
  const [records, setRecords] = useState([]);
  const [limited, setLimited] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);          // record shown in detail view (null = list)
  const [collision, setCollision] = useState(null);    // { existingId, message }
  const [status, setStatusState] = useState({ msg: '', type: '' });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [deleteArmed, setDeleteArmed] = useState(false);

  const [search, setSearch] = useState({ id: '', tagId: '', serialNumber: '', batchId: '' });
  const [quick, setQuick] = useState('');               // one box: record ID, tag UID, or serial
  const [createForm, setCreateForm] = useState({ ...emptyForm });
  const [patchForm, setPatchForm] = useState({ ...emptyForm, id: '' });
  const [loadId, setLoadId] = useState('');
  const [deleteId, setDeleteId] = useState('');

  const statusTimer = useRef(null);
  const setStatus = (msg, type = 'info', ms = 4000) => {
    setStatusState({ msg, type });
    clearTimeout(statusTimer.current);
    if (ms > 0) statusTimer.current = setTimeout(() => setStatusState({ msg: '', type: '' }), ms);
  };

  // ---- API ----
  const req = async (path, opts = {}) => {
    const res = await fetch(`${api}${path}`, { headers: authHeaders(), cache: 'no-store', ...opts });
    const data = await res.json().catch(() => ({}));
    return { res, data };
  };

  const fetchLive = useCallback(async () => {
    setStatus('Fetching latest records…', 'info', 0);
    try {
      const { res, data } = await req('/admin/bags?limit=10');
      if (!res.ok) return setStatus('Error loading records', 'error');
      setRecords(data.rows || []); setLimited(!!data.limited); setDetail(null);
      setStatus(`Loaded ${data.rows?.length || 0} record(s)`, 'success');
    } catch { setStatus('Network error', 'error'); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLive(); }, [fetchLive]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => { if (mode === 'live') fetchLive(); }, 30000);
    return () => clearInterval(t);
  }, [autoRefresh, mode, fetchLive]);

  const runSearch = async () => {
    const p = new URLSearchParams();
    if (search.id.trim()) p.set('id', search.id.trim());
    // Tag UIDs are stored without spaces, so "4D B4 53 60" must be sent as "4DB45360" to match.
    if (search.tagId.trim()) p.set('tagId', search.tagId.replace(/\s+/g, ''));
    if (search.serialNumber.trim()) p.set('serialNumber', search.serialNumber.trim());
    if (search.batchId.trim()) p.set('batchId', search.batchId.trim());
    if ([...p].length === 0) return setStatus('Enter at least one search field.', 'warn');
    setMode('search'); setStatus('Searching…', 'info', 0);
    try {
      const { res, data } = await req(`/admin/bags?${p.toString()}`);
      if (!res.ok) return setStatus('Search error', 'error');
      const rows = data.rows || [];
      setRecords(rows); setLimited(!!data.limited);
      if (rows.length === 1) { selectRecord(rows[0]); setDetail(rows[0]); } else setDetail(null);
      setStatus(rows.length === 0 ? 'No records matched.' : `Found ${rows.length}${data.limited ? '+' : ''} record(s)`, rows.length === 0 ? 'warn' : 'success');
    } catch { setStatus('Network error', 'error'); }
  };

  // One box that matches a record ID, tag UID, or serial number. Serials and record IDs are both
  // plain numbers (e.g. serial 12134 vs record 90535), so a number is looked up as both. Runs the
  // lookups in parallel, merges them, and lists exact matches first.
  const runQuickSearch = async () => {
    const term = quick.trim();
    if (!term) return setStatus('Enter a record ID, tag UID, or serial number.', 'warn');
    const compact = term.replace(/\s+/g, '');
    setMode('search'); setStatus('Searching…', 'info', 0);
    try {
      const lookups = [
        req(`/admin/bags?${new URLSearchParams({ serialNumber: term })}`),
        req(`/admin/bags?${new URLSearchParams({ tagId: compact })}`),
        /^\d+$/.test(term) ? req(`/admin/bags/${encodeURIComponent(term)}`) : null,
      ];
      const [bySerial, byTag, byId] = await Promise.all(lookups);
      if (!bySerial.res.ok || !byTag.res.ok) return setStatus('Search error', 'error');

      const merged = new Map();
      if (byId?.res.ok && byId.data?.id != null) merged.set(String(byId.data.id), byId.data);
      for (const r of [...(bySerial.data.rows || []), ...(byTag.data.rows || [])]) {
        if (!merged.has(String(r.id))) merged.set(String(r.id), r);
      }
      const lc = term.toLowerCase(), uc = compact.toUpperCase();
      const exact = r => String(r.id) === term || String(r.serialNumber || '').toLowerCase() === lc ||
        String(r.tagUid || '').toUpperCase() === uc;
      const rows = [...merged.values()].sort((a, b) => exact(b) - exact(a));
      const limitedAny = !!(bySerial.data.limited || byTag.data.limited);

      setRecords(rows); setLimited(limitedAny);
      if (rows.length === 1) { selectRecord(rows[0]); setDetail(rows[0]); } else setDetail(null);
      setStatus(rows.length === 0 ? `Nothing matched "${term}".` : `Found ${rows.length}${limitedAny ? '+' : ''} record(s)`, rows.length === 0 ? 'warn' : 'success');
    } catch { setStatus('Network error', 'error'); }
  };

  const clearSearch = () => {
    setQuick('');
    setSearch({ id: '', tagId: '', serialNumber: '', batchId: '' });
    setRecords([]); setDetail(null); setLimited(false);
  };

  const loadById = async (id) => {
    if (!id) return setStatus('Enter a record ID.', 'warn');
    setStatus(`Loading record ${id}…`, 'info', 0);
    try {
      const { res, data } = await req(`/admin/bags/${encodeURIComponent(id)}`);
      if (res.status === 404) return setStatus(`Record ${id} not found.`, 'warn');
      if (!res.ok) return setStatus('Error loading record', 'error');
      selectRecord(data); setDetail(data);
      setStatus(`Loaded record ${id}`, 'success');
      return data;
    } catch { setStatus('Network error', 'error'); }
  };

  const selectRecord = (rec) => {
    setSelected(rec);
    if (rec) {
      setPatchForm({ id: String(rec.id), tagId: rec.tagUid || '', sku: rec.model || '', serialNumber: rec.serialNumber || '', batchId: rec.batchId || '', ac: rec.ac || '' });
      setLoadId(String(rec.id)); setDeleteId(String(rec.id));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCollision(null);
    const f = createForm;
    for (const k of ['tagId', 'sku', 'serialNumber', 'batchId', 'ac']) {
      if (!f[k].trim()) return setStatus(`${k} is required.`, 'warn');
    }
    setStatus('Creating record…', 'info', 0);
    try {
      const { res, data } = await req('/admin/serialize', {
        method: 'POST',
        body: JSON.stringify({ tagUid: f.tagId, sku: f.sku, serialNumber: f.serialNumber, batchId: f.batchId, ac: f.ac }),
      });
      if (!res.ok && res.status !== 200) return setStatus(data.error || 'Create failed', 'error');
      const st = data.status;
      if (st === 'created') {
        setStatus(`Created record ID ${data.bag?.id}`, 'success');
        setCreateForm({ ...emptyForm });
        if (data.bag?.id) await loadById(data.bag.id);
      } else if (st === 'rescan') {
        setStatus('Already on file. Re-scan (order refreshed).', 'info');
        if (data.bag?.id) await loadById(data.bag.id);
      } else if (st === 'serial_conflict' || st === 'tag_conflict') {
        const ex = data.existing || {};
        const label = st === 'serial_conflict' ? 'That serial is already on a DIFFERENT tag' : 'That tag is already on a DIFFERENT serial';
        setCollision({ existingId: ex.id, message: `${label}: existing ID ${ex.id} (SN ${ex.serialNumber}, tag ${ex.tagUid}).` });
        setStatus(`Conflict: existing ID ${ex.id}`, 'warn', 0);
      } else if (st === 'claimed_order') {
        setStatus(data.message || 'That order is already claimed.', 'warn', 0);
      } else {
        setStatus(data.error || 'Could not register.', 'error');
      }
    } catch { setStatus('Network error', 'error'); }
  };

  const handlePatch = async (e) => {
    e.preventDefault();
    const id = patchForm.id.trim();
    if (!id) return setStatus('Enter the record ID to patch.', 'warn');
    const body = {};
    for (const k of ['tagId', 'sku', 'serialNumber', 'batchId', 'ac']) if (patchForm[k].trim()) body[k] = patchForm[k].trim();
    if (Object.keys(body).length === 0) return setStatus('Fill at least one field to patch.', 'warn');
    setStatus(`Patching record ${id}…`, 'info', 0);
    try {
      const { res, data } = await req(`/admin/bags/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) });
      if (!res.ok) return setStatus(data.error || 'Patch failed', 'error');
      setStatus(`Record ${id} updated`, 'success');
      selectRecord(data); setDetail(data);
    } catch { setStatus('Network error', 'error'); }
  };

  const handleDelete = async () => {
    const id = deleteId.trim();
    if (!id) return setStatus('Enter a record ID to delete.', 'warn');
    if (!deleteArmed) {
      setDeleteArmed(true);
      setTimeout(() => setDeleteArmed(false), 5000);
      return;
    }
    setDeleteArmed(false);
    setStatus(`Deleting record ${id}…`, 'info', 0);
    try {
      const { res, data } = await req(`/admin/bags/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!res.ok) return setStatus(data.error || 'Delete failed', 'error');
      setStatus(`Record ${id} deleted`, 'success');
      setRecords(prev => prev.filter(r => String(r.id) !== String(id)));
      if (selected && String(selected.id) === String(id)) { setSelected(null); setDetail(null); }
      setDeleteId('');
    } catch { setStatus('Network error', 'error'); }
  };

  const fmtCell = (rec, k) => {
    if (k === 'scanTime') return rec.scanTime ? new Date(rec.scanTime).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
    return rec[k] ?? '';
  };

  const selectedLabel = selected
    ? `ID ${selected.id}${selected.serialNumber ? ' · SN ' + selected.serialNumber : ''}${selected.tagUid ? ' · ' + selected.tagUid : ''}`
    : 'No record selected';

  return (
    <div className="bag-console">
      {/* header strip: status only (portal already has top chrome) */}
      <div className="bc-header">
        <span className="bc-title">Bag Database Console</span>
        <span className={`bc-status ${status.type}`}>{status.msg}</span>
      </div>

      {/* TOP: display */}
      <section className="bc-panel bc-panel-top">
        <div className="bc-panel-header">
          <span className="bc-mode-label">{mode === 'live' ? 'Live View' : 'Search Results'}</span>
          <span className="bc-result-count">{records.length ? `${records.length}${limited ? '+' : ''} record${records.length !== 1 ? 's' : ''}` : ''}</span>
          {detail && <button className="bc-btn bc-btn-sm" onClick={() => setDetail(null)}>← Back to List</button>}
        </div>

        {collision && (
          <div className="bc-collision" role="alert">
            <div className="bc-collision-icon">⚠</div>
            <div className="bc-collision-body">
              <strong>tagUID Collision</strong>
              <span> {collision.message}</span>
            </div>
            <div className="bc-collision-actions">
              <button className="bc-btn bc-btn-warn" onClick={() => { setLoadId(String(collision.existingId)); setActionMode('view'); const id = collision.existingId; setCollision(null); loadById(id); }}>Load Existing</button>
              <button className="bc-btn bc-btn-warn" onClick={() => { setActionMode('patch'); const id = collision.existingId; setCollision(null); loadById(id); }}>Patch Existing</button>
            </div>
            <button className="bc-btn bc-btn-ghost bc-btn-sm" onClick={() => setCollision(null)}>✕</button>
          </div>
        )}

        {detail ? (
          <div className="bc-detail">
            <table className="bc-detail-table"><tbody>
              {DETAIL_ORDER.filter(k => detail[k] !== undefined).map(k => (
                <tr key={k}><td>{k}</td><td>{k === 'scanTime' ? fmtCell(detail, k) : String(detail[k] ?? '')}</td></tr>
              ))}
            </tbody></table>
          </div>
        ) : (
          <div className="bc-results">
            {records.length === 0 ? (
              <div className="bc-empty">Use the controls below to fetch or search records.</div>
            ) : (
              <table className="bc-results-table">
                <thead><tr>{RESULT_COLS.map(c => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>
                  {records.map(rec => (
                    <tr key={rec.id} className={selected && String(selected.id) === String(rec.id) ? 'selected' : ''} onClick={() => selectRecord(rec)}>
                      {RESULT_COLS.map(c => (
                        <td key={c} className={c === 'id' ? 'id-cell' : (['tagUid', 'serialNumber'].includes(c) ? 'mono' : '')} title={String(fmtCell(rec, c))}>{String(fmtCell(rec, c))}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </section>

      {/* BOTTOM: controls */}
      <section className="bc-panel bc-panel-bottom">
        <div className="bc-controls">
          {/* LEFT: mode + live/search */}
          <div className="bc-ctrl-left">
            <div className="bc-tabs">
              <button className={`bc-tab ${mode === 'live' ? 'bc-tab-active' : ''}`} onClick={() => setMode('live')}>Live View</button>
              <button className={`bc-tab ${mode === 'search' ? 'bc-tab-active' : ''}`} onClick={() => setMode('search')}>Search</button>
            </div>
            {mode === 'live' ? (
              <div className="bc-pane">
                <p className="bc-pane-label">Show the 10 most recent records.</p>
                <div className="bc-row bc-gap">
                  <button className="bc-btn bc-btn-primary" onClick={fetchLive}>Fetch Latest</button>
                  <label className="bc-inline-label"><input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} /> Auto-refresh (30s)</label>
                </div>
              </div>
            ) : (
              <div className="bc-pane">
                <label className="bc-field-label">Record ID, Tag UID, or Serial Number
                  <input className="bc-input" value={quick} onChange={e => setQuick(e.target.value)} onKeyDown={e => e.key === 'Enter' && runQuickSearch()} placeholder="e.g. 90535, E0022302A141AF51, or 12134" autoFocus />
                </label>
                <div className="bc-row bc-gap bc-mt">
                  <button className="bc-btn bc-btn-primary" onClick={runQuickSearch}>Search</button>
                  <button className="bc-btn bc-btn-ghost" onClick={clearSearch}>Clear</button>
                </div>
                <p className="bc-pane-label bc-mt">Or search by specific fields (all filled fields must match):</p>
                <div className="bc-search-grid">
                  <label className="bc-field-label">API ID<input className="bc-input" value={search.id} onChange={e => setSearch(s => ({ ...s, id: e.target.value }))} onKeyDown={e => e.key === 'Enter' && runSearch()} placeholder="e.g. 42" /></label>
                  <label className="bc-field-label">Tag UID<input className="bc-input" value={search.tagId} onChange={e => setSearch(s => ({ ...s, tagId: e.target.value }))} onKeyDown={e => e.key === 'Enter' && runSearch()} placeholder="e.g. 4D B4 53 60" /></label>
                  <label className="bc-field-label">Serial Number<input className="bc-input" value={search.serialNumber} onChange={e => setSearch(s => ({ ...s, serialNumber: e.target.value }))} onKeyDown={e => e.key === 'Enter' && runSearch()} placeholder="e.g. SN0001" /></label>
                  <label className="bc-field-label">Batch ID<input className="bc-input" value={search.batchId} onChange={e => setSearch(s => ({ ...s, batchId: e.target.value }))} onKeyDown={e => e.key === 'Enter' && runSearch()} placeholder="e.g. 0001" /></label>
                </div>
                <div className="bc-row bc-gap bc-mt">
                  <button className="bc-btn bc-btn-ghost" onClick={runSearch}>Search by Fields</button>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: record actions */}
          <div className="bc-ctrl-right">
            <div className="bc-action-header">
              <span className="bc-pane-label">Record Actions</span>
              <span className={`bc-selected ${selected ? '' : 'muted'}`}>{selectedLabel}</span>
            </div>
            <div className="bc-action-tabs">
              {['view', 'create', 'patch', 'delete'].map(a => (
                <button key={a} className={`bc-action-tab ${actionMode === a ? 'bc-action-tab-active' : ''}`} onClick={() => setActionMode(a)}>{a[0].toUpperCase() + a.slice(1)}</button>
              ))}
            </div>

            {actionMode === 'view' && (
              <div className="bc-pane">
                <div className="bc-row bc-gap">
                  <input className="bc-input bc-input-sm" value={loadId} onChange={e => setLoadId(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadById(loadId.trim())} placeholder="Record ID" />
                  <button className="bc-btn bc-btn-primary" onClick={() => loadById(loadId.trim())}>Load Record</button>
                </div>
                <p className="muted bc-mt-sm">Or click any row in the results table above.</p>
              </div>
            )}

            {actionMode === 'create' && (
              <form className="bc-pane" onSubmit={handleCreate} autoComplete="off">
                <div className="bc-form-grid">
                  {[['tagId', 'Tag UID (tagId)', 'e.g. 4D B4 53 60'], ['sku', 'SKU', 'e.g. GFSB-MAX-M'], ['serialNumber', 'Serial Number', 'e.g. SN0001'], ['batchId', 'Batch ID', 'e.g. 0001'], ['ac', 'AC (Order #)', 'e.g. 000001']].map(([k, lbl, ph]) => (
                    <label key={k} className="bc-field-label">{lbl}<input className="bc-input" value={createForm[k]} onChange={e => setCreateForm(f => ({ ...f, [k]: e.target.value }))} placeholder={ph} /></label>
                  ))}
                </div>
                <div className="bc-row bc-gap bc-mt">
                  <button type="submit" className="bc-btn bc-btn-primary">Create Record</button>
                  <button type="button" className="bc-btn bc-btn-ghost" onClick={() => setCreateForm({ ...emptyForm })}>Reset</button>
                </div>
              </form>
            )}

            {actionMode === 'patch' && (
              <div className="bc-pane">
                <div className="bc-row bc-gap bc-mb-sm">
                  <input className="bc-input bc-input-sm" value={patchForm.id} onChange={e => setPatchForm(f => ({ ...f, id: e.target.value }))} placeholder="Record ID to patch" />
                  <button className="bc-btn bc-btn-ghost bc-btn-sm" onClick={() => loadById(patchForm.id.trim())}>Load</button>
                </div>
                <form onSubmit={handlePatch} autoComplete="off">
                  <div className="bc-form-grid">
                    {[['tagId', 'Tag UID (tagId)'], ['sku', 'SKU'], ['serialNumber', 'Serial Number'], ['batchId', 'Batch ID'], ['ac', 'AC (Order #)']].map(([k, lbl]) => (
                      <label key={k} className="bc-field-label">{lbl}<input className="bc-input" value={patchForm[k]} onChange={e => setPatchForm(f => ({ ...f, [k]: e.target.value }))} placeholder="leave blank to keep" /></label>
                    ))}
                  </div>
                  <div className="bc-row bc-gap bc-mt">
                    <button type="submit" className="bc-btn bc-btn-primary">Patch Record</button>
                  </div>
                </form>
              </div>
            )}

            {actionMode === 'delete' && (
              <div className="bc-pane">
                <div className="muted bc-mb-sm">Select a record in the table, or load one by ID first.</div>
                <div className="bc-row bc-gap">
                  <input className="bc-input bc-input-sm" value={deleteId} onChange={e => setDeleteId(e.target.value)} placeholder="Record ID" />
                  <button className="bc-btn bc-btn-danger" onClick={handleDelete}>{deleteArmed ? `Confirm delete ID ${deleteId}?` : 'Delete Record'}</button>
                </div>
                <p className="bc-warn-text bc-mt-sm">This action cannot be undone.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default SerializationConsole;
